import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch, setToken, clearToken, getToken } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [booting, setBooting] = useState(true);
  const [account, setAccount] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setAccount(null);
      return;
    }
    try {
      const data = await apiFetch("/auth/me");
      setAccount(data.account);
    } catch (e) {
      if (e.status === 401) {
        clearToken();
        setAccount(null);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setBooting(false);
    })();
  }, [refresh]);

  const commit = (data) => {
    setToken(data.token);
    setAccount(data.account);
    return data.account;
  };

  const userRegister = async (payload) => commit(await apiFetch("/auth/user/register", { method: "POST", body: { ...payload, platform: "web" }, auth: false }));
  const userLogin = async (email, password) => commit(await apiFetch("/auth/user/login", { method: "POST", body: { email, password }, auth: false }));
  const userLoginCode = async (code) => commit(await apiFetch("/auth/user/login-code", { method: "POST", body: { code }, auth: false }));
  const userQuickLogin = async (token) => commit(await apiFetch("/auth/user/quick-login", { method: "POST", body: { token }, auth: false }));
  const guestStart = async (name, learning_level) => {
    const data = await apiFetch("/auth/guest/start", { method: "POST", body: { name, learning_level }, auth: false });
    commit(data);
    return data;
  };
  const guestLogin = async (code) => commit(await apiFetch("/auth/guest/login", { method: "POST", body: { code }, auth: false }));
  const sessionLogin = async (code, name) => commit(await apiFetch("/enroll/session", { method: "POST", body: { code, name }, auth: false }));
  const forgotPassword = async (email) => apiFetch("/auth/user/forgot-password", { method: "POST", body: { email }, auth: false });
  const resetPassword = async (token, new_password) => apiFetch("/auth/user/reset-password", { method: "POST", body: { token, new_password }, auth: false });
  const portalLogin = async (role, email, password) =>
    commit(await apiFetch(`/auth/${role}/login`, { method: "POST", body: { email, password }, auth: false }));
  const developerLogin = async (email, password, code) => {
    const data = await apiFetch("/auth/developer/login", { method: "POST", body: { email, password, code }, auth: false });
    if (data.token) commit(data);
    return data;
  };
  const parentRegister = async (email, password) =>
    commit(await apiFetch("/auth/parent/register", { method: "POST", body: { email, password }, auth: false }));
  const appleSignIn = async (role, cred) => {
    const data = await apiFetch("/auth/apple", { method: "POST", body: { role, ...cred, platform: "web" }, auth: false });
    commit(data);
    return data;
  };

  const linkParentEmail = async (payload) => {
    const data = await apiFetch("/user/link-parent/email", { method: "POST", body: payload });
    setAccount(data.account);
    return data.account;
  };
  const linkParentCode = async (code) => {
    const data = await apiFetch("/user/link-parent/code", { method: "POST", body: { code } });
    setAccount(data.account);
    return data.account;
  };

  const updateSettings = async (patch) => {
    const data = await apiFetch("/user/settings", { method: "PATCH", body: patch });
    setAccount(data.account);
    return data.account;
  };

  const uploadAvatar = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const data = await apiFetch("/user/avatar/upload", { method: "POST", form });
    setAccount(data.account);
    return data.account;
  };
  const removeAvatar = async () => {
    const data = await apiFetch("/user/avatar", { method: "DELETE" });
    setAccount(data.account);
    return data.account;
  };

  const signOut = () => {
    clearToken();
    setAccount(null);
  };

  return (
    <AuthContext.Provider
      value={{
        booting,
        account,
        role: account?.role ?? null,
        refresh,
        userRegister,
        userLogin,
        userLoginCode,
        userQuickLogin,
        guestStart,
        guestLogin,
        sessionLogin,
        forgotPassword,
        resetPassword,
        portalLogin,
        developerLogin,
        parentRegister,
        appleSignIn,
        linkParentEmail,
        linkParentCode,
        updateSettings,
        uploadAvatar,
        removeAvatar,
        signOut,
        setAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
